'use client'
import { motion, useInView } from 'framer-motion'
import { useRef, useEffect, useState, useCallback } from 'react'
import SectionTitle from "@/components/ui/SectionTitle";
import Link from "next/link";
import Image from '@/components/ui/MediaImage';
import type { PageUrlMap } from "@/types/blog";
import { resolveUrlFromMap, buildLocalizedLink } from "@/lib/blog-transformers";
import { buildCategoryUrl, resolveProductTypeKeyForCategoryUrl } from "@/lib/url-helpers";
import CategoryCardSkeleton from "@/components/ui/CategoryCardSkeleton";
import { getBackendRenderAttributes } from "@/lib/backend-render-utils";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import { shouldEmbedBlockPropsInDom } from "@/lib/embed-block-props-dom";
import { sanitizeTourCatGridDescriptionHtml } from "@/lib/sanitize-category-description";

interface CategoryData {
    id: number;
    name: string;
    slug?: string;
    description?: string;
    cover_image?: string;
    /** API: cover yoksa sekme ikonu URL’i (ProductCategoryController) */
    search_tab_icon?: string;
    /** private-tours | group-tours | tour | … — net değer varsa URL tabanı buna göre (private-tours-cat …) */
    type?: string;
    parent_id?: number | null;
}

interface CatList {
    id: number; // ID'yi ekle (key prop için gerekli)
    src: string;
    alt: string;
    title: string;
    description: string;
    href: string;
}

interface TourCatGridProps {
    subTitle?: string;
    title?: string;
    description?: string;
    /**
     * Categories verisi (server-side'dan props ile gelir)
     * Eğer sağlanmazsa ve enableClientFetch true ise client-side'da çekilir
     */
    categories?: CategoryData[];
    /**
     * Category ID'leri (client-side fetch için)
     * Eğer categories props olarak sağlanmışsa, bu prop kullanılmaz
     */
    categoryIds?: number[];
    languageId?: number;
    language?: string;
    languageCode?: string;
    /**
     * Props'tan veri gelmezse client-side'da veri çekmeyi etkinleştir
     * GrapeJS preview için kullanışlıdır
     */
    enableClientFetch?: boolean;
    /**
     * Page URLs map for dynamic URL generation
     */
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
    /**
     * Kategori linkleri için ürün tipi anahtarı (group-tours → group-tours-cat).
     * resolveProps sayfa template_key ile doldurur; HomeFeatured / TourCard ile uyumlu base.
     */
    categoryUrlProductType?: string;
    __pageContext?: {
        languageId?: number;
        language?: string;
        languageCode?: string;
        pageUrls?: PageUrlMap;
        categoryUrlProductType?: string;
    };
    __grapesPreview?: boolean;
}

const DEFAULT_SUB_TITLE = 'Tour';
const DEFAULT_TITLE = 'International Tours';
const DEFAULT_DESCRIPTION =
    'Search for the best places in the world. Discover top destinations, hidden gems, and unforgettable experiences with our travel guide.';

/** Dar ekranda taşmayı önlemek için (TourCatTabs / YachtCatTabs ile aynı mantık) */
const SECTION_SHELL =
    'w-full min-w-0 max-w-full mx-auto px-4 sm:px-6 lg:w-8/12 lg:px-0 xl:w-9/12';
const SECTION_SHELL_PY = 'py-12 sm:py-20 md:py-28 lg:py-[190px]';

export default function TourCatGrid(props: TourCatGridProps = {}) {
    const {
        subTitle: propSubTitle,
        title: propTitle,
        description: propDescription,
        categories: initialCategories,
        categoryIds: propCategoryIds,
        languageId: propLanguageId,
        language,
        languageCode,
        enableClientFetch = false,
        pageUrls: propPageUrls,
        defaultLanguage: propDefaultLanguage = 'en',
        categoryUrlProductType: propCategoryUrlProductType,
        __pageContext,
        __grapesPreview = false,
        ...restProps
    } = props;

    // Her item için ayrı animasyon kontrolü yapacağız, container ref'e gerek yok
    
    const [categories, setCategories] = useState<CategoryData[]>(initialCategories || []);
    const [loading, setLoading] = useState(!initialCategories);
    const [error, setError] = useState<string | null>(null);
    const [componentProps, setComponentProps] = useState<{
        categoryIds?: number[];
        subTitle?: string;
        title?: string;
        description?: string;
    }>({});

    // Language ID'yi belirle (öncelik sırası: prop > pageContext)
    const languageId = propLanguageId || __pageContext?.languageId;
    
    // Language code'u belirle
    const resolvedLanguageCode = language || languageCode || __pageContext?.language || __pageContext?.languageCode;
    
    // PageUrls'i belirle (öncelik sırası: prop > pageContext)
    const pageUrls = propPageUrls || __pageContext?.pageUrls;

    const categoryUrlProductType =
        propCategoryUrlProductType ?? __pageContext?.categoryUrlProductType;
    
    const defaultLanguage = propDefaultLanguage;
    const resolveMediaUrl = useCallback((value?: string) => {
        if (!value) return '';
        return transformMediaUrl(value, null) || value;
    }, []);

    // İç kökte ikinci data-component-path taşıma: dış GrapesJS zaten data-component-path + data-component-props
    // taşır; çift işaretleme querySelector / data-component-props senkronunu bozuyordu.
    const backendAttributes = getBackendRenderAttributes(
        'sections/TourCatGrid',
        {
            categoryIds: propCategoryIds,
            languageId: propLanguageId,
            language,
            languageCode,
            enableClientFetch,
            pageUrls: propPageUrls,
        },
        false
    );

    // LanguageVariablesContext kullanarak tekrar eden istekleri önle
    const { getVariable } = useLanguageVariables();

    // Component hydration: data-component-props'tan props'ları al
    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) return;
        const updateProps = () => {
            const element = document.querySelector('[data-component-path="sections/TourCatGrid"]');
            if (element) {
                const propsAttr = element.getAttribute('data-component-props');
                if (propsAttr) {
                    try {
                        const props = JSON.parse(propsAttr);
                        setComponentProps(props);
                    } catch {
                        // Props parse hatası - sessizce devam
                    }
                }
            }
        };

        updateProps();

        const observer = new MutationObserver(updateProps);
        const element = document.querySelector('[data-component-path="sections/TourCatGrid"]');
        if (element) {
            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-component-props']
            });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    // categoryIds'i belirle (öncelik sırası: prop > componentProps > pageContext)
    const categoryIds = propCategoryIds || componentProps.categoryIds || undefined;

    const subTitle =
        propSubTitle ?? componentProps.subTitle ?? DEFAULT_SUB_TITLE;
    const title = propTitle ?? componentProps.title ?? DEFAULT_TITLE;
    const description =
        propDescription ?? componentProps.description ?? DEFAULT_DESCRIPTION;

    // Props'tan veri geldiyse client-side fetch yapma
    const hasInitialData = initialCategories && initialCategories.length > 0;

    useEffect(() => {
        if (!enableClientFetch) {
            setLoading(false);
            return;
        }
        // Props'tan veri geldiyse client-side fetch yapma
        if (hasInitialData) {
            setLoading(false);
            return;
        }

        async function fetchCategories() {
            // Eğer categoryIds yoksa veya boşsa, API'ye istek atma
            if (!categoryIds || categoryIds.length === 0) {
                setLoading(false);
                setCategories([]);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // API parametrelerini oluştur
                const params = new URLSearchParams();
                
                if (languageId) {
                    params.set('language_id', String(languageId));
                }

                // categoryIds varsa öncelikli olarak kullan
                if (categoryIds && categoryIds.length > 0) {
                    categoryIds.forEach(id => params.append('category_ids[]', String(id)));
                }

                const response = await fetch(`/api/product-categories?${params.toString()}`);
                
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMessage = errorData.message || errorData.error || `API error: ${response.status}`;
                    throw new Error(errorMessage);
                }

                const data = await response.json();
                // Backend'den gelen response formatı: { data: [...], pageUrls: {...} }
                // Eğer data.data varsa onu kullan, yoksa direkt data'yı kullan
                const categoriesData = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
                setCategories(categoriesData);
            } catch (err) {
                console.error('Kategoriler yüklenirken hata:', err);
                const errorMessage = err instanceof Error ? err.message : 'Kategoriler yüklenemedi';
                setError(errorMessage);
                setCategories([]);
            } finally {
                setLoading(false);
            }
        }

        fetchCategories();
    }, [languageId, categoryIds, hasInitialData, enableClientFetch]);

    // Loading state with skeleton
    if (loading) {
        return (
            <section {...backendAttributes} className={SECTION_SHELL}>
                <SectionTitle key="section-title-loading" title={title} titleTag="h2" subTitle={subTitle} subTitleTag="span" description={description} descriptionTag="p"/>
                <motion.div
                    key="categories-grid-loading" 
                    className="mt-8 grid grid-cols-1 gap-6 sm:mt-12 sm:gap-8 lg:grid-cols-2 lg:gap-10 xl:grid-cols-3"
                    initial="hidden"
                    animate="visible"
                    variants={{
                        hidden: { opacity: 0 },
                        visible: {
                            opacity: 1,
                            transition: {
                                staggerChildren: 0.1,
                                delayChildren: 0.1
                            }
                        }
                    }}
                >
                    {Array.from({ length: 6 }).map((_, index) => (
                        <CategoryCardSkeleton key={index} index={index} />
                    ))}
                </motion.div>
            </section>
        );
    }

    // Error state
    if (error) {
        return (
            <section {...backendAttributes} className={`${SECTION_SHELL} ${SECTION_SHELL_PY}`}>
                <div className="text-center text-red-500">
                    {getVariable('categories_load_error', 'Hata:')} {error}
                </div>
            </section>
        );
    }

    // Empty state - props yoksa veya kategori yoksa
    const hasData = categories.length > 0;
    const missingSelection = !enableClientFetch && !hasData && (!categoryIds || categoryIds.length === 0);
    const hasFetchedButEmpty = enableClientFetch && !loading && !hasData && (categoryIds && categoryIds.length > 0);

    if (missingSelection) {
        return (
            <section {...backendAttributes} className={`${SECTION_SHELL} ${SECTION_SHELL_PY}`}>
                <div className="text-center text-alsero-light-gray border-2 border-dashed border-gray-200 rounded-2xl p-8">
                    {getVariable('categories_no_selection', 'Bu component için kategori seçimi yapılmamış. Lütfen sayfa editöründe component\'i seçin ve "Kategorileri Seç" butonunu kullanın.')}
                </div>
            </section>
        );
    }
    
    // API'den veri çekildi ama boş geldi
    if (hasFetchedButEmpty) {
        return (
            <section {...backendAttributes} className={`${SECTION_SHELL} ${SECTION_SHELL_PY}`}>
                <div className="text-center text-alsero-light-gray">
                    {getVariable('categories_no_categories_for_language', 'Bu dil için henüz kategori bulunamadı.')}
                </div>
            </section>
        );
    }
    
    // Props var ama kategori yok veya loading sonrası boş
    if (!hasData) {
        return (
            <section {...backendAttributes} className={`${SECTION_SHELL} ${SECTION_SHELL_PY}`}>
                <div className="text-center text-alsero-light-gray">
                    {getVariable('categories_no_categories', 'Henüz kategori bulunmuyor.')}
                </div>
            </section>
        );
    }

    // Resim: API cover_image veya search_tab_icon; merkezi transform (relative storage dahil)
    const transformImageUrl = (url: string | undefined): string => {
        if (!url) {
            return 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&h=1200&fit=crop';
        }
        return transformMediaUrl(url, null) || url;
    };

    const normalizeCategoryUrl = (category: CategoryData): string => {
        const productType = resolveProductTypeKeyForCategoryUrl(
            category.type,
            categoryUrlProductType
        );
        const slug = category.slug;
        
        if (slug) {
            return buildCategoryUrl(productType, slug, pageUrls, resolvedLanguageCode || defaultLanguage, defaultLanguage);
        }
        
        // Slug yoksa, pageUrls'den base URL al veya fallback kullan
        // Category için template key: {type}-cat (örn: tour-cat, yacht-cat)
        const categoryTemplateKey = `${productType}-cat`;
        const baseUrl = resolveUrlFromMap(
            pageUrls,
            categoryTemplateKey,
            resolvedLanguageCode || defaultLanguage,
            defaultLanguage,
            `/${productType}`
        );
        return `${baseUrl}/category/${category.id}`;
    };

    // Kategorileri component formatına dönüştür
    // categories'in array olduğundan emin ol
    const Cats: CatList[] = Array.isArray(categories) ? categories.map((category) => {
        const imageUrl = transformImageUrl(
            category.cover_image || category.search_tab_icon
        );
        // Backend'den gelen URL'yi normalize et
        const href = normalizeCategoryUrl(category);
        return {
            id: category.id, // ID'yi ekle (key prop için gerekli)
            src: imageUrl,
            alt: category.name,
            title: category.name,
            description: category.description || category.name,
            href: href,
        };
    }) : [];
    return (
        <motion.section
            {...backendAttributes}
            key="panel-1"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{
                duration: 0.15,
                ease: "easeOut"
            }}
            className={SECTION_SHELL}
        >
            <SectionTitle key="section-title" title={title} titleTag="h2" subTitle={subTitle} subTitleTag="span" description={description} descriptionTag="p"/>
            <motion.div
                key="categories-grid" 
                className="mt-8 grid grid-cols-1 gap-6 sm:mt-12 sm:gap-8 lg:grid-cols-2 lg:gap-10 xl:grid-cols-3"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px" }}
                variants={{
                    hidden: { opacity: 0 },
                    visible: {
                        opacity: 1,
                        transition: {
                            staggerChildren: 0.1,
                            delayChildren: 0.1
                        }
                    }
                }}
            >
                {Cats.map((cat) => {
                    return (
                        <motion.div
                            key={cat.id}
                            variants={{
                                hidden: { opacity: 0, y: 30 },
                                visible: { opacity: 1, y: 0 }
                            }}
                            transition={{ 
                                duration: 0.5,
                                ease: "easeOut"
                            }}
                            className="group"
                        >
                        <Link
                            href={cat.href}
                            className="group flex w-full flex-col overflow-hidden rounded-lg border border-alsero-light-gray/20 bg-white transition-shadow duration-300 hover:border-alsero-light-gray/40 sm:flex-row sm:items-center sm:gap-5"
                        >
                            <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-lg bg-gray-100 sm:aspect-auto sm:h-[190px] sm:w-[190px]">
                                <Image
                                    src={resolveMediaUrl(cat.src)}
                                    alt={cat.alt}
                                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                                    fill
                                    sizes="(max-width: 639px) 100vw, 190px"
                                    loading="lazy"
                                    unoptimized={resolveMediaUrl(cat.src).startsWith('/api/media/proxy/')}
                                    placeholder="blur"
                                    blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                                />
                            </div>
                            <motion.div
                                className="min-w-0 flex-1 px-4 py-4 font-sans sm:px-0 sm:py-0"
                                        variants={{
                                            open: {
                                                opacity: 1,
                                                y: 0,
                                                transition: {
                                                    type: "spring",
                                                    stiffness: 300,
                                                    damping: 24
                                                }
                                            },
                                            closed: {
                                                opacity: 0,
                                                y: -10
                                            }
                                        }}
                            >
                                <h3 className="mb-2 break-words text-lg font-medium leading-snug text-alsero-dark-gray sm:text-xl md:text-2xl">
                                    {cat.title}
                                </h3>
                                <div
                                    className={[
                                        'category-card-desc line-clamp-4 min-w-0 leading-relaxed transition-colors',
                                        'text-sm text-alsero-light-gray sm:text-base',
                                        'group-hover:text-alsero-first',
                                        /* CMS span/strong — h etiketleri sanitize ile p’ye; tek renk, boyut tema ile */
                                        '[&_*]:!text-inherit',
                                        '[&_p]:!my-1 [&_p]:!text-sm [&_p]:!leading-relaxed [&_p]:first:!mt-0 sm:[&_p]:!text-base',
                                        '[&_span]:!text-sm [&_span]:!leading-relaxed sm:[&_span]:!text-base',
                                        '[&_strong]:!font-semibold [&_b]:!font-semibold',
                                        '[&_ul]:!my-1 [&_ol]:!my-1 [&_li]:!text-sm [&_li]:!leading-relaxed sm:[&_li]:!text-base',
                                    ].join(' ')}
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeTourCatGridDescriptionHtml(cat.description),
                                    }}
                                />
                            </motion.div>
                        </Link>
                    </motion.div>
                    );
                })}
            </motion.div>
        </motion.section>
    )
}

